import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatHeader, ChatMessages, ChatInput } from '../components/common';
import OnboardingModal from '../components/onboarding/OnboardingModal';
import ConversationSidebar from '../components/conversation/ConversationSidebar';
import { useChat, useFileUpload, useConversation } from '../hooks';
import { useAgent } from '../context/AgentContext';
import { getAgentConfig, getAgentApi } from '../agents';
import { signOut } from '../services/auth/authApi';
import { submitProfile } from '../services/api/userApi';
import { STORAGE_KEYS } from '../constants';
import '../ChatPage.css';

function Chat() {
  const navigate = useNavigate();
  const { currentAgent, initializeAgent } = useAgent();
  const {
    activateConversation,
    updateConversationList,
    addMessageToCache,
    activeConversationId,
    activeMessages,
    fetchConversations,
    resetConversations
  } = useConversation();
  const [agentConfig, setAgentConfig] = useState(null);
  const [agentApi, setAgentApi] = useState(null);
  const [onboardingRequired, setOnboardingRequired] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [onboardingError, setOnboardingError] = useState(null);
  const [, setSessionData] = useState(null);
  const [, setUserId] = useState(null);

  // Initialize agent based on backend session
  useEffect(() => {
    const loadAgent = async () => {
      try {
        // Get agent assignment from backend
        const { getSession: fetchSession, storeSession, isTokenExpired: checkTokenExpired } = await import('../services/api/sessionApi');
        
        // Check if token is expired
        if (checkTokenExpired()) {
          // Attempt silent refresh before forcing logout
          try {
            const { refreshSession } = await import('../services/auth/authApi');
            await refreshSession();
          } catch {
            resetConversations();
            await signOut();
            navigate('/login');
            return;
          }
        }
        
        const session = await fetchSession();
        
        // Store session data
        storeSession(session);
        setSessionData(session);
        setUserId(session.userId);
        
        // Schedule proactive token refresh
        import('../services/api/apiClient').then(({ scheduleTokenRefresh }) => {
          scheduleTokenRefresh();
        }).catch(() => {});
        
        // Check if onboarding is required
        if (session.onboarding_completed === false) {
          setOnboardingRequired(true);
        }
        
        // Initialize the agent assigned by backend
        // Map backend agent to Cognito group for role-based initialization
        const groupMapping = {
          'brochat': 'BroGroup',
          'crowchat': 'CrowGroup'
        };
        const userGroup = groupMapping[session.agent] || 'BroGroup';
        initializeAgent([userGroup]);
      } catch (error) {
        console.error('[ChatPage] Error loading session:', error);
        
        // Handle specific errors
        if (error.message === 'UNAUTHORIZED') {
          resetConversations();
          await signOut();
          navigate('/login');
        } else if (error.message === 'FORBIDDEN') {
          navigate('/access-denied');
        } else {
          setTimeout(() => navigate('/login'), 2000);
        }
      }
    };
    loadAgent();
  }, [initializeAgent, navigate, resetConversations]);

  // Load agent configuration and API when agent changes
  useEffect(() => {
    if (!currentAgent) return;
    
    const config = getAgentConfig(currentAgent);
    const api = getAgentApi(currentAgent);
    setAgentConfig(config);
    setAgentApi(api);
  }, [currentAgent]);

  // Fetch conversations once the agent is ready (user is authenticated)
  useEffect(() => {
    if (!currentAgent) return;
    fetchConversations();
  }, [currentAgent, fetchConversations]);

  const { 
    selectedFiles, 
    handleFileSelect, 
    removeFile,
    clearFiles,
    error: fileError,
    clearError: clearFileError,
  } = useFileUpload(['image', 'document', 'spreadsheet', 'structured']);

  /**
   * Callback handlers for conversation updates
   */
  const handleConversationCreated = (newConversation) => {
    updateConversationList(newConversation);
    // Update global activeConversationId with skipFetch=true
    // because messages are already in chatLog from useChat
    activateConversation(newConversation.conversation_id, true);
  };

  const handleConversationUpdated = (updatedConversation) => {
    updateConversationList(updatedConversation);
    // Note: Actual caching now happens in useEffect that watches chatLog
  };

  const {
    message,
    setMessage,
    chatLog,
    isLoading,
    sendMessage,
    sendQuickMessage,
    handleKeyDown,
    conversationId,
  } = useChat(selectedFiles, clearFiles, agentApi, {
    conversationId: activeConversationId,
    onConversationCreated: handleConversationCreated,
    onConversationUpdated: handleConversationUpdated
  });

  // Cache chatLog messages whenever they change for the active conversation
  // This runs AFTER chatLog is updated (unlike the callback which runs before)
  useEffect(() => {
    // Only cache if we have a conversation ID and new messages in chatLog
    // AND the chatLog is for the current active conversation (not stale from previous)
    if (activeConversationId && chatLog.length > 0 && conversationId === activeConversationId) {
      addMessageToCache(activeConversationId, chatLog);
    }
  }, [activeConversationId, chatLog, addMessageToCache, conversationId]);

  /**
   * Handle new chat button click
   */
  const handleNewChat = () => {
    // Clear everything to show fresh conversation state
    activateConversation(null); // Clears activeConversationId in context
    setMessage(''); // Clear input
    // chatLog will auto-clear when conversationId changes (useEffect in useChat)
  };

  /**
   * Handle onboarding form submission
   */
  const handleOnboardingSubmit = async (formData) => {
    setOnboardingLoading(true);
    setOnboardingError(null);

    try {
      const result = await submitProfile({
        name: formData.name,
        age: parseInt(formData.age, 10),
        sex: formData.sex
      });

      if (result.success) {
        console.log('[ChatPage] Onboarding completed successfully');
        
        // Update session data to mark onboarding as completed
        setOnboardingRequired(false);
        localStorage.setItem(STORAGE_KEYS.ONBOARDING_COMPLETED, 'true');
        
      } else {
        console.error('[ChatPage] Onboarding failed:', result.error);
        setOnboardingError(result.error);
      }
    } catch (error) {
      console.error('[ChatPage] Unexpected error during onboarding:', error);
      setOnboardingError('An unexpected error occurred. Please try again.');
    } finally {
      setOnboardingLoading(false);
    }
  };

  // Show loading state while agent initializes
  if (!agentConfig || !agentApi) {
    return (
      <div className="chat-container">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
          <p>Loading chat...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-page-wrapper">
      <ConversationSidebar onNewChat={handleNewChat} />
      <div className="chat-container">
        <OnboardingModal
          isOpen={onboardingRequired}
          onSubmit={handleOnboardingSubmit}
          isLoading={onboardingLoading}
          error={onboardingError}
        />
        
        <ChatHeader 
        isLoading={isLoading} 
        title={agentConfig.branding.title}
      />
      <ChatMessages
        chatLog={chatLog}
        isLoading={isLoading}
        conversationMessages={activeMessages}
        welcomeMessage={agentConfig.branding.welcomeMessage}
        onActionClick={sendQuickMessage}
      />
      <ChatInput
        message={message}
        onMessageChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        onSend={sendMessage}
        selectedImages={agentConfig.features.imageUpload ? selectedFiles : []}
        onImageSelect={handleFileSelect}
        onImageRemove={removeFile}
        imageError={fileError}
        onClearImageError={clearFileError}
        disabled={isLoading || onboardingRequired}
        placeholder={agentConfig.branding.placeholder}
        showImageUpload={agentConfig.features.imageUpload}
        isSending={isLoading}
        isResponseGenerating={isLoading}
      />
      </div>
    </div>
  );
}

export default Chat;
